import { Trans, useLingui } from "@lingui/react/macro";
import { DEFAULT_NEW_BOT_AVATAR_SET, GROUP_NAME_MAX_LENGTH } from "@rakazo/contracts";
import {
  BotAvatar,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
} from "@rakazo/ui-web";
import { useId, useRef, useState } from "react";
import { TEAM_TEMPLATES, type TeamTemplate } from "../../lib/team-templates";
import { errorText } from "../../lib/user-error";

/**
 * A company needs a name and a platform: the name is what the sidebar shows, the platform
 * decides which team template seeds the people. Both are required before creating.
 */
export function NewCompanyForm({
  onSubmit,
  disabled = false,
}: {
  onSubmit: (template: TeamTemplate, name: string, idempotencyKey: string) => void;
  disabled?: boolean;
}) {
  const nameId = useId();
  const attempt = useRef<{ platform: TeamTemplate["id"]; name: string; key: string } | null>(null);
  const [name, setName] = useState("");
  const [templateId, setTemplateId] = useState<TeamTemplate["id"] | null>(null);
  const template = TEAM_TEMPLATES.find((item) => item.id === templateId);
  const trimmed = name.trim();

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (!trimmed || !template || disabled) return;
        if (attempt.current?.name !== trimmed || attempt.current.platform !== template.id) {
          attempt.current = { name: trimmed, platform: template.id, key: crypto.randomUUID() };
        }
        onSubmit(template, trimmed, attempt.current.key);
      }}
    >
      <div className="flex items-center gap-3">
        <label htmlFor={nameId} className="w-20 shrink-0 text-sm text-muted-foreground">
          <Trans>Company name</Trans>
        </label>
        <Input
          id={nameId}
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={GROUP_NAME_MAX_LENGTH}
          className="flex-1"
          data-testid="company-name"
        />
      </div>
      <p className="mt-4 text-sm text-muted-foreground">
        <Trans>Company type</Trans>
      </p>
      <div className="mt-2 grid grid-cols-2 gap-2">
        {TEAM_TEMPLATES.map((option) => {
          const selected = option.id === templateId;
          return (
            <button
              key={option.id}
              type="button"
              data-testid={`team-card-${option.id}`}
              aria-pressed={selected}
              disabled={disabled}
              onClick={() => setTemplateId(option.id)}
              className={`flex flex-col items-start gap-2 rounded-xl border px-3 py-3 text-start transition-colors disabled:opacity-60 ${
                selected ? "border-ring bg-accent" : "border-border hover:bg-accent"
              }`}
            >
              <span className="flex shrink-0 items-center">
                {[option.leader, ...option.members].map((member, index) => (
                  <span key={member.key} className={index === 0 ? "" : "-ms-2"}>
                    <BotAvatar
                      avatarSet={DEFAULT_NEW_BOT_AVATAR_SET}
                      color={member.color}
                      identity={`${option.id}:${member.key}`}
                      size={26}
                    />
                  </span>
                ))}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-[15px] font-medium text-foreground">
                  {option.name}
                </span>
                <span className="mt-0.5 block text-label text-muted-foreground">
                  {option.summary}
                </span>
              </span>
            </button>
          );
        })}
      </div>
      <Button type="submit" className="mt-4 w-full" disabled={disabled || !trimmed || !template}>
        <Trans>Create</Trans>
      </Button>
    </form>
  );
}

/** Mounted only while open; the parent unmounts it from `onCancel` or after `onCreate`. */
export function CreateTeamDialog({
  onCancel,
  onCreate,
}: {
  onCancel: () => void;
  onCreate: (template: TeamTemplate, name: string, idempotencyKey: string) => Promise<void>;
}) {
  const { t } = useLingui();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create(template: TeamTemplate, name: string, idempotencyKey: string) {
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await onCreate(template, name, idempotencyKey);
    } catch (err) {
      setError(errorText(err, t`Could not create team`));
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !submitting) onCancel();
      }}
    >
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        className="sm:max-w-md"
        data-testid="create-team-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            <Trans>New company</Trans>
          </DialogTitle>
        </DialogHeader>
        <NewCompanyForm
          onSubmit={(template, name, idempotencyKey) => void create(template, name, idempotencyKey)}
          disabled={submitting}
        />
        {error ? <p className="mt-1 text-sm text-destructive">{error}</p> : null}
      </DialogContent>
    </Dialog>
  );
}
